import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAllQuestionSets, deleteQuestionSet } from '../../api/listeningQuestionSetApi';
import {
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Card,
    CardContent,
    Avatar,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Quiz as QuizIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Add as AddIcon,
    Hearing as HearingIcon,
    Info as InfoIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';
import BreadcrumbsNav from '../../components/common/BreadcrumbsNav';

const ListeningQuestionSetList = () => {
    const { listeningId } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [questionSets, setQuestionSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPart, setSelectedPart] = useState('part1');
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [viewDetails, setViewDetails] = useState(null);

    useEffect(() => {
        const fetchQuestionSets = async () => {
            try {
                const { data } = await getAllQuestionSets(listeningId, selectedPart);
                setQuestionSets(data);
            } catch (error) {
                console.error('Failed to fetch question sets:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestionSets();
    }, [listeningId, selectedPart]);

    const handlePartChange = (part) => {
        setSelectedPart(part);
    };

    const handleCreateQuestionSet = () => {
        navigate(`/listenings/${listeningId}/question-sets/new`, {
            state: { part: selectedPart },
        });
    };

    const handleEditQuestionSet = (questionSetId) => {
        navigate(`/listenings/${listeningId}/question-sets/${questionSetId}/edit`, {
            state: { part: selectedPart },
        });
    };

    const handleViewQuestions = (questionSetId) => {
        navigate(`/listenings/${listeningId}/question-sets/${questionSetId}/questions`, {
            state: { part: selectedPart },
        });
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteQuestionSet(listeningId, selectedPart, deleteTarget._id);
            setQuestionSets((prev) => prev.filter((qs) => qs._id !== deleteTarget._id));
            showToast('Question set deleted successfully!', 'success');
        } catch (error) {
            console.error('Failed to delete question set:', error);
            showToast('Failed to delete question set.', 'error');
        } finally {
            setDeleteTarget(null);
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <BreadcrumbsNav
                    links={[{ label: 'Listenings', onClick: () => navigate('/listenings') }]}
                    current="Question Sets"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: 'primary.main',
                            width: 48,
                            height: 48,
                            mr: 2,
                        }}
                    >
                        <HearingIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Listening Question Sets
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Create and manage question sets for listening parts
                        </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <ModernButton
                        onClick={handleCreateQuestionSet}
                        startIcon={<AddIcon />}
                        variant="contained"
                    >
                        Create Question Set
                    </ModernButton>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            label={`${questionSets.length} Question Sets`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Part Selection */}
            <Card sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                            <HearingIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Select Part
                        </Typography>
                    </Box>
                    
                    <ToggleButtonGroup
                        color="primary"
                        exclusive
                        value={selectedPart}
                        onChange={(e, val) => val && handlePartChange(val)}
                        sx={{
                            gap: 2,
                            '& .MuiToggleButton-root': {
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                px: 3,
                                py: 1.5,
                                border: '2px solid',
                                borderColor: 'grey.300',
                                margin: '0 4px',
                                minWidth: '80px',
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    },
                                },
                                '&:hover': {
                                    backgroundColor: 'primary.50',
                                    borderColor: 'primary.main',
                                },
                            },
                        }}
                    >
                        {["part1", "part2", "part3", "part4"].map((part) => (
                            <ToggleButton key={part} value={part}>
                                {part.charAt(0).toUpperCase() + part.slice(1)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </CardContent>
            </Card>

            {/* Question Sets Data Grid */}
            {loading ? (
                <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 8, textAlign: 'center' }}>
                        <CircularProgress size={48} />
                        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                            Loading question sets...
                        </Typography>
                    </CardContent>
                </Card>
            ) : questionSets.length === 0 ? (
                <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 8, textAlign: 'center' }}>
                        <Avatar sx={{ backgroundColor: 'grey.300', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                            <QuizIcon sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
                            No Question Sets Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                            No question sets found for {selectedPart}. Create your first question set to get started.
                        </Typography>
                        <ModernButton
                            onClick={handleCreateQuestionSet}
                            startIcon={<AddIcon />}
                            variant="contained"
                        >
                            Create First Question Set
                        </ModernButton>
                    </CardContent>
                </Card>
            ) : (
                <ModernDataGrid
                    rows={questionSets}
                    getRowId={(row) => row._id}
                    columns={[
                        { 
                            field: 'instructionLabel', 
                            headerName: 'Instruction Label', 
                            flex: 1,
                            renderCell: (params) => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar
                                        sx={{
                                            backgroundColor: 'primary.main',
                                            width: 32,
                                            height: 32,
                                            fontSize: '0.875rem',
                                        }}
                                    >
                                        Q
                                    </Avatar>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {params.value || "N/A"}
                                    </Typography>
                                </Box>
                            ),
                        },
                        {
                            field: 'type',
                            headerName: 'Question Type',
                            flex: 1,
                            renderCell: (params) => (
                                <Chip
                                    label={params.value || "N/A"}
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontFamily: 'monospace', fontWeight: 500 }}
                                />
                            ),
                        },
                        {
                            field: 'questionCount',
                            headerName: 'Questions',
                            flex: 0.8,
                            renderCell: (params) => (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                                        {params.value || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        questions
                                    </Typography>
                                </Box>
                            ),
                        },
                        {
                            field: 'actions',
                            headerName: 'Actions',
                            sortable: false,
                            flex: 1.5,
                            renderCell: (params) => (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="View Details">
                                        <IconButton
                                            onClick={() => setViewDetails(params.row)}
                                            sx={{
                                                color: 'info.main',
                                                '&:hover': {
                                                    backgroundColor: 'info.50',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="View Questions">
                                        <IconButton
                                            onClick={() => handleViewQuestions(params.row._id)}
                                            sx={{
                                                color: 'warning.main',
                                                '&:hover': {
                                                    backgroundColor: 'warning.50',
                                                },
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit Question Set">
                                        <IconButton
                                            onClick={() => handleEditQuestionSet(params.row._id)}
                                            sx={{
                                                color: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: 'primary.50',
                                                },
                                            }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete Question Set">
                                        <IconButton
                                            onClick={() => setDeleteTarget(params.row)}
                                            sx={{
                                                color: 'error.main',
                                                '&:hover': {
                                                    backgroundColor: 'error.50',
                                                },
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            ),
                        },
                    ]}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={Boolean(deleteTarget)} 
                onClose={() => setDeleteTarget(null)}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                    Confirm Deletion
                </DialogTitle>
                <DialogContent sx={{ pb: 2 }}>
                    <Typography>
                        Are you sure you want to delete the question set "{deleteTarget?.instructionLabel}"? 
                        This action cannot be undone and will also delete all associated questions.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <ModernButton
                        onClick={() => setDeleteTarget(null)}
                        variant="outlined"
                    >
                        Cancel
                    </ModernButton>
                    <ModernButton
                        onClick={handleDelete}
                        variant="contained"
                        sx={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                            },
                        }}
                    >
                        Delete
                    </ModernButton>
                </DialogActions>
            </Dialog>

            {/* Question Set Details Dialog */}
            <Dialog 
                open={Boolean(viewDetails)} 
                onClose={() => setViewDetails(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        p: 1,
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
                    Question Set Details
                </DialogTitle>
                <DialogContent sx={{ pb: 2 }}>
                    {viewDetails && (
                        <Box sx={{ mt: 2 }}>
                            {/* Question Set Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        width: 48,
                                        height: 48,
                                        mr: 2,
                                    }}
                                >
                                    Q
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {viewDetails.instructionLabel || "N/A"}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        <Chip
                                            label={viewDetails.type || "N/A"}
                                            color="info"
                                            size="small"
                                            variant="outlined"
                                            sx={{ fontFamily: 'monospace' }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Question Set Information */}
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                            Question Set Information
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Type:</strong> {viewDetails.type || "N/A"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Question Count:</strong> {viewDetails.questionCount || 0}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Part:</strong> {selectedPart}
                                        </Typography>
                                    </Card>
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2, backgroundColor: 'grey.50' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                            Content Details
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Instruction:</strong> {viewDetails.instructionLabel || "N/A"}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Status:</strong> Active
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Questions:</strong> {viewDetails.questionCount || 0} available
                                        </Typography>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <ModernButton
                        onClick={() => setViewDetails(null)}
                        variant="outlined"
                    >
                        Close
                    </ModernButton>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ListeningQuestionSetList;
